import React, { useContext, useEffect, useRef, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";

import { AppContext } from "../../../App";
import { agreementStatuses, amhStatuses, callStatuses, fmStatuses, ipStatuses, migrationStatuses, scStatuses } from "../../../util/statuses";
import { useNavigate, useParams } from "react-router-dom";
import { saveAs } from "file-saver";
import CheckIcon from "../../icons/CheckIcon";
import MarkReadButton from "./MarkReadButton";

const SMSChat = () => {
    const [user, setUser] = useState(null);
    const [chatLoaded, setChatLoaded] = useState(false);
    const [hasDupes, setHasDupes] = useState(false);
    const [filteredNotes, setFilteredNotes] = useState([]);
    const [selectedChat, setSelectedChat] = useState([]);
    const [refreshChat, setRefreshChat] = useState(0);
    const [refreshUser, setRefreshUser] = useState(0);
    const [notesModal, setNotesModal] = useState(false);
    const [messageInput, setMessageInput] = useState("");
    const [hasError, setHasError] = useState(null);
    const [viewModalSource, setViewModalSource] = useState(null);

    const navigate = useNavigate();

    const { userId } = useParams();

    const { loggedInUser, request, emitter } = useContext(AppContext);

    const chatWindowRef = useRef(null);
    const inputRef = useRef(null);

    emitter.on("message", (message) => {
        if (message.content === userId) {
            setRefreshChat((prev) => prev + 1);
        }
    });

    emitter.on("status-callback", (message) => {
        if (message.content === userId) {
            setRefreshChat((prev) => prev + 1);
        }
    });

    emitter.on("text-consent", (message) => {
        if (message.content === userId) {
            setRefreshUser((prev) => prev + 1);
        }
    });

    const scrollToInput = () => {
        if (inputRef.current) {
            inputRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    useEffect(() => {
        const chatWindow = chatWindowRef.current;
        if (chatWindow) {
            chatWindow.scrollTop = chatWindow.scrollHeight;
        }

        scrollToInput();
    }, [selectedChat]);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await request({
                    url: `/staff/sms/${userId}/user`,
                    method: "GET",
                });

                const { user, hasDupes } = response;
                setUser(user);
                setHasDupes(hasDupes);
                setFilteredNotes(user.notes);
                setSelectedChat([]);

                if (user.firstName && user.lastName) {
                    document.title = `Staff | SMS | ${user.firstName} ${user.lastName[0]}.`;
                }
            } catch (err) {
                console.log(err);
                setHasError(err);
            }
        };

        fetchUser();
    }, [request, userId, refreshUser]);

    useEffect(() => {
        setChatLoaded(false);
    }, [userId]);

    useEffect(() => {
        const fetchChat = async () => {
            try {
                const response = await request({
                    url: `/staff/sms/${user.id}/texts`,
                    method: "GET",
                });

                const { texts } = response;
                setSelectedChat(texts.reverse());
                setChatLoaded(true);
            } catch (err) {
                console.log(err);
                setHasError(err);
            }
        };

        if (user) {
            fetchChat();
        }
    }, [request, user, refreshChat]);

    useEffect(() => {
        if (hasError) {
            throw hasError;
        }
    }, [hasError]);

    const markRead = async (text) => {
        try {
            const response = await request({
                url: `/staff/sms/toggle-read`,
                method: "POST",
                data: {
                    textID: text.id,
                },
            });
        } catch (err) {
            console.log(err);
            setHasError(err);
        }
    };

    const sendMessage = async () => {
        try {
            const response = await request({
                url: `/staff/sms/send-text`,
                method: "POST",
                data: {
                    to: user.id,
                    from: loggedInUser.id,
                    body: messageInput,
                },
            });

            setMessageInput("");
        } catch (err) {
            console.log(err);
            setHasError(err);
        }
    };

    const handleInputChange = (event, setState) => {
        setState(event.target.value);
    };

    const extractTime = (dateTimeString) => {
        const date = new Date(dateTimeString);

        const options = { hour: "numeric", minute: "2-digit" };
        return date.toLocaleTimeString("en-US", options);
    };

    const extractDate = (dateTimeString) => {
        const date = new Date(dateTimeString);
        const now = new Date();

        const isCurrentYear = date.getFullYear() === now.getFullYear();
        const isCurrentMonth = date.getMonth() === now.getMonth();
        const isCurrentDay = date.getDate() === now.getDate();

        let options = {};

        if (isCurrentYear && isCurrentMonth && isCurrentDay) {
            return "Today";
        } else if (isCurrentYear) {
            options = { month: "long", day: "numeric" };
        } else {
            options = { year: "numeric", month: "numeric", day: "numeric" };
        }

        return date.toLocaleDateString("en-US", options);
    };

    const extractDateTime = (dateTimeString) => {
        const options = {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            timeZoneName: "short",
        };
        const formattedDate = new Date(dateTimeString).toLocaleDateString("en-US", options);
        return formattedDate;
    };

    const closeNotesModal = () => {
        setFilteredNotes(user.notes);
        setNotesModal(false);
    };

    const openProfile = () => {
        const currentUrl = new URL(window.location.href);

        // Update the pathname of the URL
        currentUrl.pathname = `/staff/split-members/${userId}`;

        return currentUrl.href;
    };

    if (!user) {
        return (
            <div className="flex flex-grow h-full justify-center items-center z-50">
                <img width={65} height={65} src={`${process.env.PUBLIC_URL}/images/loading_spinner.gif`} alt="Loading..." />
            </div>
        );
    }

    const renderReadBy = (text, i) => {
        if (text.from_id !== user.id || !text.read) {
            return <div></div>;
        }
        // check next text message
        const totalIndices = selectedChat.length - 1;

        // if there is a next message and they are the owner, do not render
        if (i + 1 <= totalIndices && selectedChat[i + 1].from_id === user.id) {
            return <div></div>;
        }

        var label;
        if (text.unread) {
            const hasMarkedUnread = new Date(text.unread) > new Date(text.read);

            label = (
                <span className="flex flex-wrap">
                    <span class="flex flex-shrink-0 mr-1">
                        <CheckIcon className="inline-block" />
                        {hasMarkedUnread ? "Unread" : "Read"} by{" "}
                    </span>
                    <span className="font-medium mr-1">
                        {text.read_user.firstName} {text.read_user.lastName}
                    </span>{" "}
                    on {extractDateTime(text.read)}
                </span>
            );
        }

        // otherwise if i+1 is greater than total indices or the next index is not a text from them render
        return <div className={`font-body font-normal text-xs text-black-600 items-center mb-2 md:mb-0 md:flex-grow`}>{text.unread ? label : extractDateTime(text.read)}</div>;
    };

    const determineBadgeContent = (user) => {
        if (
            user.freeze_member &&
            ["frozen_selected", "frozen_signed", "frozen_screening", "frozen_first_cycling", "frozen_second_cycling", "frozen_cycle_complete"].includes(user.freeze_member.cycle_first_status)
        ) {
            return (
                <svg width="10" height="12" viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M5.00031 0.733334C5.33168 0.733334 5.60031 1.00196 5.60031 1.33333V1.76251L5.66531 1.73001C5.9617 1.58182 6.3221 1.70195 6.4703 1.99834C6.61849 2.29473 6.49836 2.65513 6.20197 2.80332L5.60031 3.10415V4.01905L6.41869 4.48825L7.20809 4.03248L7.16778 3.36102C7.14792 3.03025 7.39997 2.746 7.73074 2.72615C8.06152 2.70629 8.34576 2.95834 8.36562 3.28911L8.36997 3.36164L8.74164 3.14706C9.02861 2.98137 9.39557 3.07969 9.56126 3.36666C9.72695 3.65363 9.62862 4.02059 9.34165 4.18628L8.96999 4.40086L9.03062 4.44089C9.30715 4.62347 9.38332 4.99565 9.20074 5.27219C9.01816 5.54872 8.64598 5.62489 8.36944 5.44231L7.80811 5.0717L7.01578 5.52916L7.01851 6.47241L7.80811 6.9283L8.36944 6.55769C8.64598 6.37511 9.01816 6.45128 9.20074 6.72781C9.38332 7.00435 9.30715 7.37653 9.03062 7.55911L8.96999 7.59914L9.34166 7.81372C9.62863 7.97941 9.72695 8.34637 9.56126 8.63334C9.39557 8.92031 9.02862 9.01863 8.74164 8.85295L8.36998 8.63836L8.36562 8.71089C8.34576 9.04167 8.06152 9.29371 7.73074 9.27385C7.39997 9.254 7.14792 8.96975 7.16778 8.63898L7.20809 7.96752L6.4158 7.51008L5.60031 7.9841V8.89585L6.20197 9.19668C6.49836 9.34487 6.61849 9.70527 6.4703 10.0017C6.3221 10.298 5.9617 10.4182 5.66531 10.27L5.60031 10.2375V10.6667C5.60031 10.998 5.33168 11.2667 5.00031 11.2667C4.66894 11.2667 4.40031 10.998 4.40031 10.6667V10.2375L4.3353 10.27C4.03892 10.4182 3.67851 10.298 3.53032 10.0017C3.38212 9.70527 3.50226 9.34487 3.79865 9.19668L4.40031 8.89585V7.98095L3.58194 7.51175L2.79253 7.96753L2.83284 8.63898C2.8527 8.96975 2.60065 9.254 2.26987 9.27385C1.9391 9.29371 1.65485 9.04167 1.635 8.71089L1.63064 8.63837L1.25899 8.85295C0.97202 9.01863 0.605065 8.92031 0.439375 8.63334C0.273686 8.34637 0.372007 7.97941 0.65898 7.81372L1.03063 7.59914L0.969997 7.55911C0.693462 7.37653 0.617295 7.00435 0.799873 6.72781C0.982452 6.45128 1.35464 6.37511 1.63117 6.55769L2.19252 6.92831L2.98485 6.47084L2.98212 5.52759L2.19252 5.07169L1.63117 5.44231C1.35464 5.62489 0.982452 5.54872 0.799873 5.27219C0.617295 4.99565 0.693462 4.62347 0.969997 4.44089L1.03063 4.40086L0.65898 4.18628C0.372007 4.02059 0.273686 3.65363 0.439375 3.36666C0.605065 3.07969 0.97202 2.98137 1.25899 3.14706L1.63064 3.36163L1.635 3.28911C1.65485 2.95834 1.9391 2.70629 2.26987 2.72615C2.60065 2.746 2.8527 3.03025 2.83284 3.36102L2.79253 4.03247L3.58484 4.48992L4.40031 4.01592V3.10415L3.79865 2.80332C3.50226 2.65513 3.38212 2.29473 3.53032 1.99834C3.67851 1.70195 4.03892 1.58182 4.3353 1.73001L4.40031 1.76251V1.33333C4.40031 1.00196 4.66894 0.733334 5.00031 0.733334ZM4.99759 5.05673L4.18214 5.53073L4.18487 6.47419L5.00303 6.94328L5.81849 6.46927L5.81577 5.52581L4.99759 5.05673Z"
                        fill="#393939"
                    />
                </svg>
            );
        } else if (user.freeze_member && ["not_a_candidate_for_frozen", "frozen_declined", "frozen_disqualified"].includes(user.freeze_member.cycle_first_status)) {
            return (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M2.09444 2.03842C3.11973 1.14901 4.68319 0.666656 6.86016 0.666656H8.73336C9.06473 0.666656 9.33336 0.935286 9.33336 1.26666V2.19999C9.33336 2.21146 9.33303 2.22292 9.33237 2.23437C9.21079 4.35289 8.62366 5.9164 7.59169 6.94877C6.5539 7.98696 5.15484 8.39999 3.60002 8.39999H2.47699C2.4121 8.67486 2.3633 8.96363 2.32968 9.26625C2.29308 9.59559 1.99643 9.83292 1.66709 9.79632C1.33774 9.75973 1.10042 9.46308 1.13702 9.13373C1.18835 8.67177 1.27215 8.22476 1.39443 7.79673C0.662419 6.83537 0.660156 5.91384 0.660156 5.46666L0.660159 5.46498C0.663764 4.17721 1.05446 2.94059 2.09444 2.03842ZM2.90939 7.19999C2.98719 7.04793 3.07261 6.90192 3.16591 6.76198C3.62188 6.07802 4.28726 5.50669 5.2437 5.08161C5.54651 4.94703 5.68288 4.59245 5.5483 4.28964C5.41372 3.98683 5.05914 3.85045 4.75633 3.98504C3.61277 4.49329 2.76148 5.20529 2.16745 6.09634C2.1031 6.19287 2.04201 6.29112 1.9841 6.39101C1.86014 6.02309 1.86015 5.70171 1.86016 5.47156L1.86016 5.46753C1.86324 4.42233 2.17257 3.55924 2.88077 2.94489C3.60385 2.31764 4.83712 1.86666 6.86016 1.86666H8.13336V2.18258C8.01952 4.11988 7.49174 5.35137 6.74299 6.10041C5.99788 6.84582 4.94694 7.19999 3.60002 7.19999H2.90939Z"
                        fill="#393939"
                    />
                </svg>
            );
        }

        return <span>{user.freeze_member ? "SM" : "IP"}</span>;
    };

    const determineDaySplit = (i, dateTimeString) => {
        let time = "";
        if (i === 0) {
            time = extractDate(dateTimeString);
        } else {
            const dateString = extractDate(dateTimeString);
            const lastDateString = extractDate(selectedChat[i - 1].created_at);
            if (dateString !== lastDateString) {
                time = dateString;
            }
        }

        if (time) {
            return (
                <div className="flex w-full justify-center items-center my-4">
                    <div className="w-full mr-5 border-b border-white-300" />
                    <div className="border border-white-300 font-body font-medium text-xs text-black-600 py-1 px-2 rounded-full text-nowrap">{time}</div>
                    <div className="w-full ml-5 border-b border-white-300" />
                </div>
            );
        }
    };

    const determineMessageStatus = (text) => {
        var icon, label;
        if (text.status === "failed") {
            icon = (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M7 4.33333V7M7 9.66667H7.00667M1 7C1 7.78793 1.15519 8.56815 1.45672 9.2961C1.75825 10.0241 2.20021 10.6855 2.75736 11.2426C3.31451 11.7998 3.97595 12.2417 4.7039 12.5433C5.43185 12.8448 6.21207 13 7 13C7.78793 13 8.56815 12.8448 9.2961 12.5433C10.0241 12.2417 10.6855 11.7998 11.2426 11.2426C11.7998 10.6855 12.2417 10.0241 12.5433 9.2961C12.8448 8.56815 13 7.78793 13 7C13 5.4087 12.3679 3.88258 11.2426 2.75736C10.1174 1.63214 8.5913 1 7 1C5.4087 1 3.88258 1.63214 2.75736 2.75736C1.63214 3.88258 1 5.4087 1 7Z"
                        stroke="#DF2927"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
            label = <div className="ml-1">Failed to send</div>;
        } else if (text.status === "delivered") {
            icon = (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="" className="fill-green-600 bi bi-check-all" viewBox="0 0 16 16">
                    <path d="M8.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L2.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093L8.95 4.992zm-.92 5.14.92.92a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 1 0-1.091-1.028L9.477 9.417l-.485-.486z" />
                </svg>
            );
            label = <div className="ml-1">Delivered</div>;
        } else {
            icon = <CheckIcon className="fill-green-600" />;
            label = <div className="ml-1">Sent</div>;
        }

        return (
            <div className={`flex w-full mr-14 text-xs text-right justify-end`}>
                {icon}
                {label}
            </div>
        );
    };

    const determineInput = () => {
        if (!user.text_consent && !selectedChat.length) {
            return null;
        }

        return (
            <div className="relative w-full px-10 pt-4 pb-8">
                <TextareaAutosize
                    autoFocus
                    className="w-full p-4 pr-14 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                    disabled={!user.text_consent}
                    minRows={1}
                    maxRows={7}
                    placeholder="Write your SMS here..."
                    value={messageInput}
                    onChange={(e) => handleInputChange(e, setMessageInput)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                        }
                    }}
                />
                <div
                    onClick={user.text_consent ? sendMessage : null}
                    className={`absolute bottom-11 right-12 flex items-center bg-black rounded-full w-10 h-10 justify-center ${!user.text_consent ? "opacity-50" : "cursor-pointer"}`}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="fill-white bi bi-send" viewBox="0 0 16 16">
                        <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576zm6.787-8.201L1.591 6.602l4.339 2.76z" />
                    </svg>
                </div>
            </div>
        );
    };

    const openView = (url) => {
        setViewModalSource(url);
    };

    const closeView = () => {
        setViewModalSource(null);
    };

    const handleDownload = (imageUrl, imageName) => {
        saveAs(imageUrl, imageName);
    };

    return (
        <div className="flex flex-col h-full">
            {notesModal ? (
                <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="modal-content w-full max-w-3xl bg-white p-10 rounded-md shadow-md relative max-h-[500]">
                        <div>
                            <h3 className="text-2xl font-semibold mb-4">
                                <span className="heading-underline-forest-50 ">
                                    {user.donorCode ? `[${user.donorCode}]` : ""} {user.firstName} {user.lastName}
                                </span>
                            </h3>
                            <input
                                onChange={(e) => {
                                    let query = e.target.value;

                                    let filteredNotes = structuredClone(user.notes);

                                    filteredNotes = filteredNotes.filter((note) => note.note.includes(query.toLowerCase()));

                                    setFilteredNotes(filteredNotes);
                                }}
                                className="w-full py-5 px-5 rounded-lg border border-gray-300 mb-1"
                                placeholder="Search notes..."
                            />
                            <div className="overflow-y-auto max-h-[300px] w-full ml-2 divide-y divide-dashed">
                                {filteredNotes ? (
                                    filteredNotes.map((note) => (
                                        <div key={note.id} className={`flex w-full my-3 ${note.pinned ? "bg-yellow-100" : "bg-gray-100"} rounded-md pt-3 pl-3`}>
                                            <div className="w-7 mr-2 mt-1 flex">
                                                <h1 className="flex items-center justify-center w-7 h-7 rounded-full px-2 py-1 text-black text-sm" style={{ backgroundColor: "#D2E9E6" }}>
                                                    {note.staff.firstName.charAt(0).toUpperCase()}
                                                </h1>
                                            </div>
                                            <div className="flex flex-col text-sm w-full">
                                                <div className="flex justify-between items-center">
                                                    <span>
                                                        <span className="font-semibold ">
                                                            {note.staff.firstName} {note.staff.lastName[0]}.
                                                        </span>
                                                        <span className="text-gray-400 ml-2">| Note</span>
                                                    </span>
                                                    <span className="mr-3 flex items-center">
                                                        <span className="mr-2">
                                                            <button className="focus:outline-none">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    className={`h-3 w-3 fill-current ${note.pinned ? "text-yellow-600" : "text-gray-500"}`}
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <path d="M12 .587l3.668 7.568 8.332 1.151-6.064 5.828 1.48 8.279-7.416-3.967-7.417 3.967 1.481-8.279-6.064-5.828 8.332-1.151z" />
                                                                </svg>
                                                            </button>
                                                        </span>
                                                        <span className="text-gray-400">{extractDate(note.created_at)}</span>
                                                    </span>
                                                </div>
                                                <div className="pt-2 pb-4 break-words whitespace-pre-line">{note.note}</div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div>This user has no notes!</div>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-4 rounded-md p-4">
                                <div className="col-span-2 flex justify-end mt-4">
                                    <button onClick={closeNotesModal} className="mr-2 px-4 py-2 rounded-md bg-gray-300 hover:bg-gray-400">
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {viewModalSource ? (
                <div
                    className="modal"
                    style={{
                        position: "fixed",
                        top: "0",
                        left: "0",
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 30,
                    }}
                >
                    <div
                        className="modal-content w-full max-w-3xl"
                        style={{
                            backgroundColor: "white",
                            padding: "40px",
                            borderRadius: "5px",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                            height: "80%",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <h3 className="text-2xl font-semibold mb-4">
                            <span className="heading-underline-forest-50 ">Photo view</span>
                        </h3>
                        <div className="h-[70%] flex flex-grow items-center justify-center">
                            <img className="max-h-full max-w-full" src={viewModalSource} alt="imgToView" />
                        </div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-4 rounded-md p-4">
                            <div className="col-span-2 flex justify-end mt-4">
                                <button onClick={closeView} className="mr-2 px-4 py-2 rounded-md bg-gray-300 hover:bg-gray-400">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {!window.location.pathname.includes("split-members") && !window.location.pathname.includes("intended-parents") ? (
                <div className={`py-4 px-8 h-29 flex justify-between border-b border-white-200 bg-white`}>
                    <div className="flex">
                        <div className="flex mr-2">
                            {user.user_photos ? (
                                <div className={`relative w-16 h-16 rounded-full px-2 py-1 text-black text-xl mr-2`} style={{ backgroundColor: "#D2E9E6" }}>
                                    <img src={user.user_photos[0].cdn_url} alt={user.user_photos[0].name} className="absolute inset-0 w-full h-full object-cover rounded-full" />

                                    <div
                                        className={`absolute -bottom-1 -right-1 font-bold text-[7px] font-body border border-white-100 ${
                                            user.freeze_member ? "bg-primary text-secondary" : "bg-family-apricot text-black"
                                        } rounded-full w-6 h-6 flex items-center justify-center`}
                                    >
                                        {determineBadgeContent(user)}
                                    </div>
                                </div>
                            ) : (
                                <div className={`col-span-1 flex justify-center`}>
                                    <div className="relative flex justify-center items-center mr-2 px-2 py-1 col-span-2 w-16 h-16 rounded-full bg-secondary text-white">
                                        <div>{user.firstName ? user.firstName.charAt(0).toUpperCase() : "?"}</div>
                                        <div
                                            className={`absolute -bottom-1 -right-1 font-bold text-[7px] font-body border border-white-100 ${
                                                user.freeze_member ? "bg-primary text-secondary" : "bg-family-apricot text-black"
                                            } rounded-full w-6 h-6 flex items-center justify-center`}
                                        >
                                            {determineBadgeContent(user)}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <h3 className="flex">
                                <a className="flex font-body font-bold text-base" href={openProfile()} target="_blank" rel="noreferrer">
                                    {user.donorCode ? <div className="mr-1">[{user.donorCode}]</div> : null}
                                    <div>
                                        {user.firstName} {user.lastName}
                                    </div>
                                </a>
                            </h3>

                            {user.freeze_member ? (
                                <div className="col-span-8 grid grid-cols-3 gap-1">
                                    <div
                                        className="h-6 text-secondary px-2 py-1 rounded-md border border-white-300 font-body font-medium text-xs flex items-center"
                                        style={{
                                            backgroundColor: fmStatuses.filter((status) => status.value === user.freeze_member.status)[0].bg,
                                            color: fmStatuses.filter((status) => status.value === user.freeze_member.status)[0].color,
                                        }}
                                    >
                                        {fmStatuses.filter((status) => status.value === user.freeze_member.status)[0].label}
                                    </div>
                                    <div
                                        className="h-6 text-secondary px-2 py-1 rounded-md border border-white-300 font-body font-medium text-xs flex items-center"
                                        style={{
                                            backgroundColor: scStatuses.filter((status) => status.value === user.freeze_member.cycle_first_status)[0].bg,
                                            color: scStatuses.filter((status) => status.value === user.freeze_member.cycle_first_status)[0].color,
                                        }}
                                    >
                                        {scStatuses.filter((status) => status.value === user.freeze_member.cycle_first_status)[0].label}
                                    </div>
                                    <div
                                        className="h-6 text-secondary px-2 py-1 rounded-md border border-white-300 font-body font-medium text-xs flex items-center"
                                        style={{
                                            backgroundColor: amhStatuses.filter((status) => status.value === user.freeze_member.amh_status)[0].bg,
                                            color: amhStatuses.filter((status) => status.value === user.freeze_member.amh_status)[0].color,
                                        }}
                                    >
                                        {amhStatuses.filter((status) => status.value === user.freeze_member.amh_status)[0].label}
                                    </div>
                                    <div
                                        className="h-6 text-secondary px-2 py-1 rounded-md border border-white-300 font-body font-medium text-xs flex items-center"
                                        style={{
                                            backgroundColor: callStatuses.filter((status) => status.value === user.freeze_member.call_status)[0].bg,
                                            color: callStatuses.filter((status) => status.value === user.freeze_member.call_status)[0].color,
                                        }}
                                    >
                                        {callStatuses.filter((status) => status.value === user.freeze_member.call_status)[0].label}
                                    </div>
                                    <div
                                        className="h-6 text-secondary px-2 py-1 rounded-md border border-white-300 font-body font-medium text-xs flex items-center"
                                        style={{
                                            backgroundColor: agreementStatuses.filter((status) => status.value === user.freeze_member.agreement_status)[0].bg,
                                            color: agreementStatuses.filter((status) => status.value === user.freeze_member.agreement_status)[0].color,
                                        }}
                                    >
                                        {agreementStatuses.filter((status) => status.value === user.freeze_member.agreement_status)[0].label}
                                    </div>
                                    {user.migration_status ? (
                                        <div
                                            className="h-6 text-secondary px-2 py-1 rounded-md border border-white-300 font-body font-medium text-xs flex items-center"
                                            style={{
                                                backgroundColor: migrationStatuses.filter((status) => status.value === user.migration_status)[0].bg,
                                                color: migrationStatuses.filter((status) => status.value === user.migration_status)[0].color,
                                            }}
                                        >
                                            {migrationStatuses.filter((status) => status.value === user.migration_status)[0].label}
                                        </div>
                                    ) : null}
                                </div>
                            ) : (
                                <div className="col-span-8 grid grid-cols-3 gap-1">
                                    <div
                                        className="h-6 text-secondary px-2 py-1 rounded-md border border-white-300 font-body font-medium text-xs flex items-center"
                                        style={{
                                            backgroundColor: ipStatuses.filter((status) => status.value === user.intended_parent.family.status)[0].bg,
                                            color: ipStatuses.filter((status) => status.value === user.intended_parent.family.status)[0].color,
                                        }}
                                    >
                                        {ipStatuses.filter((status) => status.value === user.intended_parent.family.status)[0].label}
                                    </div>
                                    {user.migration_status ? (
                                        <div
                                            className="h-6 text-secondary px-2 py-1 rounded-md border border-white-300 font-body font-medium text-xs flex items-center"
                                            style={{
                                                backgroundColor: migrationStatuses.filter((status) => status.value === user.migration_status)[0].bg,
                                                color: migrationStatuses.filter((status) => status.value === user.migration_status)[0].color,
                                            }}
                                        >
                                            {migrationStatuses.filter((status) => status.value === user.migration_status)[0].label}
                                        </div>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-x-2">
                        <div
                            onClick={() => {
                                if (user.freeze_member) {
                                    navigate(`/staff/split-members/${userId}`);
                                } else {
                                    navigate(`/staff/intended-parents/${userId}`);
                                }
                            }}
                            className="flex border border-white-100 rounded-full w-10 h-10 justify-center items-center cursor-pointer"
                        >
                            <svg width="14" height="20" viewBox="0 0 14 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M1 19V17C1 15.9391 1.42143 14.9217 2.17157 14.1716C2.92172 13.4214 3.93913 13 5 13H9C10.0609 13 11.0783 13.4214 11.8284 14.1716C12.5786 14.9217 13 15.9391 13 17V19M3 5C3 6.06087 3.42143 7.07828 4.17157 7.82843C4.92172 8.57857 5.93913 9 7 9C8.06087 9 9.07828 8.57857 9.82843 7.82843C10.5786 7.07828 11 6.06087 11 5C11 3.93913 10.5786 2.92172 9.82843 2.17157C9.07828 1.42143 8.06087 1 7 1C5.93913 1 4.92172 1.42143 4.17157 2.17157C3.42143 2.92172 3 3.93913 3 5Z"
                                    stroke="#6B6B6B"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <div onClick={() => setNotesModal(!notesModal)} className="flex border border-white-100 rounded-full w-10 h-10 justify-center items-center cursor-pointer bg-black">
                            <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M5 5H11M5 9H11M5 13H9M1 3C1 2.46957 1.21071 1.96086 1.58579 1.58579C1.96086 1.21071 2.46957 1 3 1H13C13.5304 1 14.0391 1.21071 14.4142 1.58579C14.7893 1.96086 15 2.46957 15 3V17C15 17.5304 14.7893 18.0391 14.4142 18.4142C14.0391 18.7893 13.5304 19 13 19H3C2.46957 19 1.96086 18.7893 1.58579 18.4142C1.21071 18.0391 1 17.5304 1 17V3Z"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    </div>
                </div>
            ) : null}
            {hasDupes ? (
                <div className={`flex items-center py-3 px-8 border border-[#FFF0BB] bg-[#FFF8DD]`}>
                    <img alt="Cofertility logo" className="pr-2" src={`${process.env.PUBLIC_URL}/images/warning_triangle.svg`} />
                    <div className="font-body font-medium text-sm">This phone number is linked to multiple profiles. Messages will appear in all associated threads</div>
                </div>
            ) : null}
            {chatLoaded && user.text_consent && !selectedChat.length ? (
                <div className="min-h-screen flex flex-col items-center justify-center">
                    <img alt="Cofertility logo" src={`${process.env.PUBLIC_URL}/images/sms_thread_empty.svg`} />
                    <h3 className="w-1/2 font-display font-normal text-2xl whitespace-pre-line text-center mt-6 mb-2">No text message history for this user</h3>
                </div>
            ) : null}
            {chatLoaded && !user.text_consent && !selectedChat.length ? (
                <div className="min-h-screen flex flex-col items-center justify-center">
                    <img alt="Cofertility logo" src={`${process.env.PUBLIC_URL}/images/sms_no_consent.svg`} />
                    <h3 className="w-1/2 font-display font-normal text-2xl whitespace-pre-line text-center mt-6 mb-2">SMS disabled for this member</h3>
                    <div className="w-1/3 font-body font-light text-sm text-center">
                        This member hasn't consented to receive SMS messages. Send a follow-up email to encourage enabling SMS updates.
                    </div>
                </div>
            ) : null}

            <div
                ref={chatWindowRef}
                className={`w-full flex-grow overflow-y-auto ${window.location.pathname.includes("split-members") || window.location.pathname.includes("intended-parents") ? "px-32" : "px-10"}`}
            >
                {selectedChat.map((text, i) => {
                    return (
                        <div key={text.id} className={`flex flex-row flex-wrap w-full my-4 ${text.from_id !== user.id ? "justify-end" : ""}`}>
                            {determineDaySplit(i, text.created_at)}

                            <div className={`flex w-full ${text.from_id !== user.id ? "text-right justify-end mr-14" : "ml-14"}`}>
                                <div className="mr-1 font-body font-medium text-sm text-black-600">
                                    {text.from_id !== user.id ? `${text.from_user.firstName} ${text.from_user.lastName}` : `${user.firstName} ${user.lastName}`}
                                </div>
                                <div className="font-body font-normal text-sm text-black-600">| {extractTime(text.created_at)}</div>
                            </div>

                            {text.from_id === user.id ? (
                                <div className="flex flex-col justify-end mb-2">
                                    {user.user_photos ? (
                                        <div className={`mr-2 flex justify-center items-center`}>
                                            <div className={`relative w-10 h-10 rounded-full px-2 py-1 text-black text-xl mr-2`} style={{ backgroundColor: "#D2E9E6" }}>
                                                <img src={user.user_photos[0].cdn_url} alt={user.user_photos[0].name} className="absolute inset-0 w-full h-full object-cover rounded-full" />

                                                <div
                                                    className={`absolute -bottom-1 -right-1 font-bold text-[7px] font-body border border-white-100 ${
                                                        user.freeze_member ? "bg-primary text-secondary" : "bg-family-apricot text-black"
                                                    } rounded-full w-4 h-4 flex items-center justify-center`}
                                                >
                                                    {determineBadgeContent(user)}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={`col-span-2 flex justify-center items-center`}>
                                            <div className="flex justify-center items-center mr-2 px-2 py-1 col-span-2 w-10 h-10 rounded-full bg-secondary">
                                                {user.firstName ? user.firstName.charAt(0).toUpperCase() : "?"}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : null}

                            <div
                                className={`${
                                    window.location.pathname.includes("split-members") || window.location.pathname.includes("intended-parents") ? "w-1/2" : "w-3/5"
                                } border border-black rounded-t-2xl my-2 p-4 ${
                                    text.from_id !== user.id ? "justify-between rounded-l-2xl bg-white border border-white-200" : "rounded-r-2xl bg-[#F1FEE0] border border-[#CAF293]"
                                }`}
                            >
                                <div className="items-center">
                                    <div className="w-full">
                                        {text.photo_id ? (
                                            <div className="relative inline-block">
                                                <img alt="sms" src={text.photo.cdn_url} />
                                                <div
                                                    onClick={() => openView(text.photo.cdn_url)}
                                                    className="absolute top-2 right-2 rounded-full bg-secondary p-1 border border-white flex items-center justify-center cursor-pointer"
                                                    style={{ zIndex: 10 }}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" className="bi bi-eye" viewBox="0 0 16 16">
                                                        <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z" />
                                                        <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0" />
                                                    </svg>
                                                </div>
                                                <div
                                                    onClick={() => handleDownload(text.photo.cdn_url, text.photo.name)}
                                                    className="absolute top-10 right-2 rounded-full bg-secondary p-1 border border-white flex items-center justify-center cursor-pointer"
                                                    style={{ zIndex: 10 }}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" className="bi bi-download" viewBox="0 0 16 16">
                                                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                                        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                                                    </svg>
                                                </div>
                                            </div>
                                        ) : null}
                                        <div>{text.body}</div>
                                    </div>

                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between pt-3">
                                        {renderReadBy(text, i)}
                                        {user.id === text.from_id && i === selectedChat.length - 1 ? <MarkReadButton text={text} markRead={markRead} /> : null}
                                    </div>
                                </div>
                            </div>

                            {text.to_id === user.id ? (
                                <div className="flex flex-col justify-end ml-2 mb-2">
                                    <div className={`col-span-2 flex justify-center items-center`}>
                                        <div className="flex justify-center items-center mr-2 px-2 py-1 col-span-2 w-10 h-10 rounded-full bg-secondary text-white">
                                            {text.from_user.firstName ? text.from_user.firstName.charAt(0).toUpperCase() : "?"}
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                            {text.from_id !== user.id ? determineMessageStatus(text) : null}
                        </div>
                    );
                })}

                {!user.text_consent && selectedChat.length ? (
                    <div className="">
                        <div className={`w-full flex rounded-2xl my-2 p-4 bg-[#FFF8DD] border border-[#FFF0BB]`}>
                            <div className="pt-[3px] mr-2">
                                <img alt="Cofertility logo" src={`${process.env.PUBLIC_URL}/images/info_warning.svg`} />
                            </div>
                            <div>
                                <div className="font-body font-medium text-base">Member Opted Out of SMS</div>
                                <div className="font-body font-normal text-sm">This member has withdrawn consent and will no longer receive text messages from our team.</div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>

            <div ref={inputRef} className="flex w-full justify-center">
                {determineInput()}
            </div>
        </div>
    );
};

export default SMSChat;
